
import request from '@/shared/apis'

// Evaluation Status constants
export const EvaluationStatus = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  SUBMITTED: 'SUBMITTED',
  REVIEWED: 'REVIEWED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED'
};

// URL helper functions

// URL helper functions
const getBaseApiUrl = () => {
  return '/evaluations';
};

const getEvaluationApiUrl = (evaluationId) => {
  return `${getBaseApiUrl()}/${evaluationId}`;
};

const getEvaluatorApiUrl = (evaluatorId) => {
  return `${getBaseApiUrl()}/evaluator/${evaluatorId}`;
};

const getEvaluateeApiUrl = (evaluateeId) => {
  return `${getBaseApiUrl()}/evaluatee/${evaluateeId}`;
};

/**
 * Get all evaluations without pagination
 * Optional query parameters: sessionId, year, departmentId, facultyId, status, evaluateeId, sort, direction
 */
export const getAllEvaluations = async (filters) => {


  let queryParams = `user=${filters && filters.userId}`;

  // Prefer sessionId over year
  if (filters && filters.sessionId) {
    queryParams += `&sessionId=${filters.sessionId}`;
  }

  if (filters) {
    if (filters.departmentId) queryParams += `&departmentId=${filters.departmentId}`;
    if (filters.facultyId) queryParams += `&facultyId=${filters.facultyId}`;
    if (filters.status) queryParams += `&status=${filters.status}`;
    if (filters.templateId) queryParams += `&templateId=${filters.templateId}`;
    if (filters.student) queryParams += `&student=${encodeURIComponent(filters.student)}`;
if (filters.sort) queryParams += `&sort=${filters.sort}`;
    if (filters.direction) queryParams += `&direction=${filters.direction}`;
  }

  return await request.get(`${getBaseApiUrl()}/all?${queryParams}`);
};

/**
 * Get paginated evaluations
 * @param page Page number (0-based)
 * @param size Page size
 * @param filters Optional filters like sessionId, year, departmentId, status, etc.
 */
export const getPaginatedEvaluations = async (
  page = 0,
  size = 10,
  filters
) => {
  let queryParams = `page=${page}&size=${size}`;

  if (filters) {
    // Prefer sessionId over year
    if (filters.sessionId) queryParams += `&sessionId=${filters.sessionId}`;
    else if (filters.year) queryParams += `&year=${filters.year}`;

    if (filters.departmentId) queryParams += `&departmentId=${filters.departmentId}`;
    if (filters.facultyId) queryParams += `&facultyId=${filters.facultyId}`;
    if (filters.status) queryParams += `&status=${filters.status}`;
    if (filters.evaluateeId) queryParams += `&evaluateeId=${filters.evaluateeId}`;
    if (filters.templateId) queryParams += `&templateId=${filters.templateId}`;
    if (filters.student) queryParams += `&student=${encodeURIComponent(filters.student)}`;
    if (filters.sort) queryParams += `&sort=${filters.sort}`;
    if (filters.direction) queryParams += `&direction=${filters.direction}`;
  }

  return await request.get(`${getBaseApiUrl()}/paginated?${queryParams}`);
};

/**
 * Get evaluations by evaluator
 * @param evaluatorId The ID of the evaluator
 */
export const getEvaluationsByEvaluator = async (evaluatorId) => {
  return await request.get(getEvaluatorApiUrl(evaluatorId));
};

/**
 * Get all evaluations for a specific evaluatee without pagination
 * @param evaluateeId The ID of the evaluatee (staff being evaluated)
 */
export const getEvaluationsByEvaluatee = async (evaluateeId) => {
  return await request.get(`${getEvaluateeApiUrl(evaluateeId)}/all`);
};

/**
 * Get paginated evaluations for a specific evaluatee
 * @param evaluateeId The ID of the evaluatee (staff being evaluated)
 * @param page Page number (0-based)
 * @param size Page size
 * @param filters Optional filters like year, status, etc.
 */
export const getPaginatedEvaluationsByEvaluatee = async (
  evaluateeId,
  page = 0,
  size = 10,
  filters
) => {
  let queryParams = `page=${page}&size=${size}`;

  if (filters) {
    if (filters.year) queryParams += `&year=${filters.year}`;
    if (filters.status) queryParams += `&status=${filters.status}`;
    if (filters.departmentId) queryParams += `&departmentId=${filters.departmentId}`;
    if (filters.facultyId) queryParams += `&facultyId=${filters.facultyId}`;
    if (filters.sort) queryParams += `&sort=${filters.sort}`;
    if (filters.direction) queryParams += `&direction=${filters.direction}`;
  }

  return await request.get(`${getEvaluateeApiUrl(evaluateeId)}?${queryParams}`);
};

/**
 * Get a specific evaluation by ID
 * @param id The ID of the evaluation
 */
export const getEvaluationById = async (id) => {
  return await request.get(getEvaluationApiUrl(id));
};

/**
 * Create a new evaluation
 * @param data Evaluation data object containing:
 *   - templateId: The ID of the evaluation template to use
 *   - evaluatorId: The ID of the user who will evaluate
 *   - evaluateeId: The ID of the user being evaluated
 *   - title: The title of the evaluation
 *   - startDate: The start date of the evaluation period (ISO format: YYYY-MM-DD)
 *   - endDate: The end date of the evaluation period (ISO format: YYYY-MM-DD)
 *   - sessionId: The session ID (preferred over year)
 *   - year: The year of the evaluation (fallback if sessionId not provided)
 */
export const createEvaluation = async (data) => {
  const params = new URLSearchParams();

  // Required fields
  params.append('templateId', data.templateId);
  params.append('evaluatorId', data.evaluatorId);
  params.append('evaluateeId', data.evaluateeId);
  params.append('startDate', data.startDate);
  params.append('endDate', data.endDate);

  // Prefer sessionId over year
  if (data.sessionId) {
    params.append('sessionId', data.sessionId);
  }

  return await request.post(`${getBaseApiUrl()}?${params}`);
};

/**
 * Get all responses for an evaluation
 * @param evaluationId The ID of the evaluation
 */
export const getEvaluationResponses = async (evaluationId) => {
  return await request.get(`${getEvaluationApiUrl(evaluationId)}/responses`);
};

/**
 * Save a response for a field in an evaluation
 * @param evaluationId The ID of the evaluation
 * @param responseData The response data to save
 * @throws {Error} If the section is submitted and cannot be edited
 */
export const saveResponse = async (evaluationId, responseData) => {
  try {
    return await request.post(`${getEvaluationApiUrl(evaluationId)}/responses`, responseData);
  } catch (error) {
    // Handle section submission errors
    if (error.response.status === 400 && error.response.data.message.includes('submitted section')) {
      throw new Error('Cannot modify responses in a submitted section');
    }
    throw error;
  }
};


/**
 * Update the status of an evaluation
 * @param id The ID of the evaluation
 * @param status The new status for the evaluation
 */
export const updateEvaluationStatus = async (id, status) => {
  return await request.put(`${getEvaluationApiUrl(id)}/status?status=${status}`);
};

/**
 * Delete an evaluation
 * @param id The ID of the evaluation to delete
 */
export const deleteEvaluation = async (id) => {
  return await request.delete(getEvaluationApiUrl(id));
};

/**
 * Get evaluation summary data
 * @param id The ID of the evaluation
 */
export const getEvaluationSummary = async (id) => {
  return await request.get(`${getEvaluationApiUrl(id)}/summary`);
};

/**
 * Download department evaluation report as PDF
 * @param templateId Required - Evaluation template ID to filter the report
 * @param departmentId Optional department ID (defaults to user's department)
 * @param sessionId Optional evaluation session ID
 * @returns PDF blob
 */
export const downloadDepartmentEvaluationReport = async (templateId, departmentId, sessionId) => {
  const params = { templateId };
  if (departmentId) params.departmentId = departmentId;
  if (sessionId) params.sessionId = sessionId;

  const queryString = '?' + new URLSearchParams(params).toString();
  return await request.get(`${getBaseApiUrl()}/department-report${queryString}`, {
    responseType: 'blob'
  });
};
